import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NAV_LINKS, navy, navyMid, blue } from "./data";

// ── Button ────────────────────────────────────────────────────────────────────
export const Button = ({ children, variant = "primary", onClick, className = "", to }) => {
  const navigate = useNavigate();
  const base = "inline-flex items-center justify-center font-semibold rounded-sm transition-all duration-200 cursor-pointer select-none";
  const variants = {
    primary:   "bg-blue-600 hover:bg-blue-700 text-white px-7 py-3.5 shadow-lg hover:shadow-blue-600/30 hover:shadow-xl tracking-wide text-sm uppercase",
    secondary: "border-2 border-white text-white hover:bg-white hover:text-navy px-7 py-3.5 tracking-wide text-sm uppercase",
    outline:   "border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white px-7 py-3.5 tracking-wide text-sm uppercase",
  };
  const handleClick = () => {
    if (to) navigate(to);
    else if (onClick) onClick();
  };
  return (
    <button className={`${base} ${variants[variant]} ${className}`} onClick={handleClick}>
      {children}
    </button>
  );
};

// ── Section label ─────────────────────────────────────────────────────────────
export const SectionLabel = ({ children }) => (
  <p className="text-blue-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">{children}</p>
);

// ── Section heading ───────────────────────────────────────────────────────────
export const SectionHeading = ({ children, light = false }) => (
  <h2 className="text-3xl md:text-4xl font-bold leading-tight" style={{ color: light ? "#fff" : navy }}>
    {children}
  </h2>
);

// ── Page hero ─────────────────────────────────────────────────────────────────
export const PageHero = ({ label, title, subtitle, light = true }) => (
  <section
    className="pt-32 pb-20 relative"
    style={{ background: light ? `linear-gradient(135deg, ${navy} 0%, #1a3260 100%)` : "#f8faff" }}
  >
    {light && (
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: "linear-gradient(rgba(255,255,255,0.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.5) 1px,transparent 1px)",
        backgroundSize: "60px 60px",
      }} />
    )}
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      {label && <SectionLabel>{label}</SectionLabel>}
      <h1
        className="text-4xl md:text-5xl font-bold leading-tight mb-5"
        style={{ color: light ? "#fff" : navy }}
      >
        {title}
      </h1>
      {subtitle && (
        <p
          className="text-lg max-w-2xl mx-auto leading-relaxed"
          style={{ color: light ? "#9ca3af" : "#6b7280" }}
        >
          {subtitle}
        </p>
      )}
    </div>
  </section>
);

// ── Navbar ────────────────────────────────────────────────────────────────────
export const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled]   = useState(false);
  const location = useLocation();
  const navigate  = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "rgba(15,30,60,0.97)" : "rgba(15,30,60,0.88)",
        backdropFilter: "blur(12px)",
        borderBottom: scrolled ? "1px solid rgba(255,255,255,0.08)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between py-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img
            src="/logo-icon.png"
            alt="VedaLogic"
            style={{ height: "36px", width: "auto" }}
          />
          <span className="text-white font-bold text-lg tracking-tight">VedaLogic</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map(({ label, path }) => (
            <Link
              key={path}
              to={path}
              className={`text-sm font-medium transition-colors duration-150 tracking-wide ${
                location.pathname === path ? "text-white" : "text-gray-300 hover:text-white"
              }`}
            >
              {label}
            </Link>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden lg:block">
          <Button onClick={() => navigate("/contact")}>Book Your AI Strategy Call</Button>
        </div>

        {/* Mobile hamburger */}
        <button className="lg:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)}>
          <div className="w-6 h-0.5 bg-white mb-1.5" />
          <div className="w-6 h-0.5 bg-white mb-1.5" />
          <div className="w-6 h-0.5 bg-white" />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="lg:hidden px-6 pb-6 pt-2" style={{ background: navy }}>
          {NAV_LINKS.map(({ label, path }) => (
            <Link
              key={path}
              to={path}
              className="block w-full text-left text-gray-300 hover:text-white py-3 text-sm font-medium border-b border-white/10"
            >
              {label}
            </Link>
          ))}
          <Button className="mt-4 w-full" onClick={() => navigate("/contact")}>
            Book Your AI Strategy Call
          </Button>
        </div>
      )}
    </header>
  );
};

// ── Footer ────────────────────────────────────────────────────────────────────
export const Footer = () => {
  const navigate = useNavigate();
  return (
    <footer style={{ background: navy }} className="py-12 border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo-icon.png" alt="VedaLogic" style={{ height: "28px", width: "auto" }} />
              <span className="text-white font-bold text-base">VedaLogic</span>
            </div>
            <p className="text-gray-400 text-sm mb-2">AI Automation for Operations-Heavy Businesses</p>
            <p className="text-gray-500 text-xs leading-relaxed">AI consulting and implementation for service businesses and operations-heavy industries.</p>
          </div>
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-4">Navigation</h4>
            <div className="space-y-2">
              {NAV_LINKS.map(({ label, path }) => (
                <Link key={path} to={path} className="block text-gray-400 hover:text-white text-sm transition-colors">{label}</Link>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-4">Get In Touch</h4>
            <button onClick={() => navigate("/contact")} className="text-gray-400 hover:text-white text-sm transition-colors block mb-2">Contact Us</button>
            <button onClick={() => navigate("/contact")} className="text-gray-400 hover:text-white text-sm transition-colors block mb-2">Book a Strategy Call</button>
            <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="text-gray-400 hover:text-white text-sm transition-colors block">LinkedIn →</a>
          </div>
          <div>
            <h4 className="text-white font-bold text-sm uppercase tracking-widest mb-4">Our Ecosystem</h4>
            <a
              href="https://vedaworks.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white text-sm transition-colors block mb-1"
            >
              VedaWorks.ai →
            </a>
            <p className="text-gray-600 text-xs leading-relaxed">Fractional CPO · CTO · AI Leadership</p>
          </div>
        </div>
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-gray-600 text-xs">© {new Date().getFullYear()} VedaLogic. All rights reserved.</p>
          <p className="text-gray-600 text-xs">AI Automation & Operational Intelligence</p>
        </div>
      </div>
    </footer>
  );
};

// ── Layout wrapper ────────────────────────────────────────────────────────────
export const Layout = ({ children }) => (
  <div className="font-sans antialiased" style={{ color: navy }}>
    <Navbar />
    <main>{children}</main>
    <Footer />
  </div>
);